/**
 * script-ipc.ts — Protocol definitions for parent/child IPC.
 *
 * Transport: newline-delimited JSON over the child's stdin/stdout.
 * stderr is passed through for uncaught exceptions and compile errors.
 *
 * See backend/docs/SCRIPT-FLOW-IMPLEMENTATION.md § IPC Bridge.
 */

// ============================================
// Parent → Child
// ============================================

export interface InitMessage {
	type: 'init';
	/**
	 * Params passed to getData(). Date/date_range values cross the IPC boundary
	 * as ISO strings and are restored to Date objects in the child via paramSchema.
	 */
	params: Record<string, any>;
	/** Frozen timestamp for ctx.now — ISO string */
	now: string;
	/**
	 * Maps each parameter name to its declared type so the child can restore
	 * Date objects (lost during JSON serialization).
	 */
	paramSchema?: Record<string, 'string' | 'number' | 'date' | 'date_range' | 'enum' | 'boolean'>;
}

export interface QueryResultMessage {
	type: 'query_result';
	/** Matches the `id` from the child's QUERY message */
	id: string;
	data: any[];
	count: number;
	metadata?: {
		totalCount?: number;
		executionTimeMs?: number;
	};
}

export interface QueryErrorMessage {
	type: 'query_error';
	id: string;
	error: string;
}

/**
 * Response to a `run_tool` request — carries whatever the direct-tool's fn()
 * returned. Shape is arbitrary; the script is expected to know what its chosen
 * tool yields and unwrap accordingly.
 */
export interface RunToolResultMessage {
	type: 'run_tool_result';
	id: string;
	result: any;
}

export type ParentToChildMessage =
	| InitMessage
	| QueryResultMessage
	| QueryErrorMessage
	| RunToolResultMessage;

// ============================================
// Child → Parent
// ============================================

export interface QueryMessage {
	type: 'query';
	/** Short uuid — parent uses this to correlate its response */
	id: string;
	toolId: string;
	sql: string;
}

/**
 * Run an arbitrary tool with structured params. Used by ctx.runTool() to call
 * direct tools (and any tool whose `fn(params)` doesn't expect a SQL string).
 * Reply is RunToolResultMessage or QueryErrorMessage.
 */
export interface RunToolMessage {
	type: 'run_tool';
	id: string;
	toolId: string;
	params: Record<string, any>;
}

export interface StreamMessage {
	type: 'stream';
	chunk: string;
}

export interface ExecutedQuery {
	sourceId: string;
	sourceName: string;
	sql: string;
	data: any[];
	count: number;
	totalCount?: number;
	executionTimeMs: number;
	/**
	 * When the script registered a computed dataset via ctx.emit(), this is
	 * marked virtual so component generation can distinguish it from a real SQL query.
	 */
	virtual?: boolean;
}

export interface ResultMessage {
	type: 'result';
	data: any[];
	count: number;
	executedQueries: ExecutedQuery[];
	executionTimeMs: number;
	/** First 5 rows of the final `data` — lets the parent judge intent-match cheaply */
	sampleRows: any[];
}

export interface ErrorMessage {
	type: 'error';
	message: string;
	stack?: string;
	/** "compile" → tsx couldn't parse; "runtime" → thrown during getData */
	phase?: 'compile' | 'runtime';
}

export type ChildToParentMessage =
	| QueryMessage
	| RunToolMessage
	| StreamMessage
	| ResultMessage
	| ErrorMessage;

// ============================================
// Utilities
// ============================================

/**
 * Encode a message for transport: JSON + newline.
 */
export function encodeMessage(msg: ParentToChildMessage | ChildToParentMessage): string {
	return JSON.stringify(msg) + '\n';
}

/**
 * Thrown when a single NDJSON line grows past the byte ceiling without a
 * newline — a runaway writer or a pathologically large message. Callers abort
 * (kill the child / exit) instead of buffering toward OOM. See #9 in
 * backend/docs/SCRIPT-FLOW-SCALING-ISSUES.md.
 */
export class LineOverflowError extends Error {
	constructor(public readonly buffered: number, public readonly limit: number) {
		super(`IPC message exceeded ${limit} bytes without a newline (${buffered} buffered) — aborting to avoid unbounded memory.`);
		this.name = 'LineOverflowError';
	}
}

/** Default per-message ceiling — generous enough for large result sets, low
 *  enough to catch a runaway/infinite writer. Override via SCRIPT_MAX_IPC_BYTES. */
const DEFAULT_MAX_IPC_BYTES = (() => {
	const envVal = Number(process.env.SCRIPT_MAX_IPC_BYTES);
	return Number.isFinite(envVal) && envVal > 0 ? Math.floor(envVal) : 64 * 1024 * 1024;
})();

/**
 * Split a stream of data chunks on newlines and emit each complete JSON line.
 * Partial lines are buffered until the next chunk arrives, up to `maxBytes` —
 * past which `push` throws `LineOverflowError` rather than grow unbounded.
 */
export class LineSplitter {
	private buffer = '';

	constructor(private readonly maxBytes: number = DEFAULT_MAX_IPC_BYTES) {}

	push(chunk: string): string[] {
		this.buffer += chunk;
		const lines: string[] = [];
		let idx: number;
		while ((idx = this.buffer.indexOf('\n')) !== -1) {
			const line = this.buffer.slice(0, idx);
			this.buffer = this.buffer.slice(idx + 1);
			if (line.length > 0) lines.push(line);
		}
		// A partial line past the ceiling means no newline is coming (or the
		// message is pathologically large). Drop the buffer and signal abort.
		if (this.buffer.length > this.maxBytes) {
			const buffered = this.buffer.length;
			this.buffer = '';
			throw new LineOverflowError(buffered, this.maxBytes);
		}
		return lines;
	}

	/** Flush any remaining partial data (useful on stream close) */
	flush(): string | null {
		if (this.buffer.length === 0) return null;
		const rest = this.buffer;
		this.buffer = '';
		return rest;
	}
}

/**
 * Generate a short correlation id for in-flight queries.
 * Collision within a single execution is astronomically unlikely.
 */
export function shortId(): string {
	return Math.random().toString(36).slice(2, 10);
}
